import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { QrCode, Lock, KeyRound, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { QRCode } from '../../components/ui/QRCode';
import { fetchWithAuth } from '../../hooks/useAuth';

interface TwoFactorSetup {
  secret: string;
  qrCode: string;
}

export function TwoFactorAuthPage() {
  const queryClient = useQueryClient();
  const [setupMode, setSetupMode] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');

  // Fetch 2FA status
  const { data } = useQuery({
    queryKey: ['current-user'],
    queryFn: async () => {
      const response = await fetchWithAuth('/api/admin/auth/check');
      if (!response.ok) throw new Error('Failed to fetch user data');
      return response.json();
    },
  });

  // Setup 2FA
  const setupMutation = useMutation({
    mutationFn: async () => {
      const response = await fetchWithAuth('/api/admin/auth/2fa/setup', {
        method: 'POST'
      });
      if (!response.ok) throw new Error('Failed to setup 2FA');
      return response.json() as Promise<TwoFactorSetup>;
    },
    onSuccess: () => {
      setSetupMode(true);
    },
    onError: () => {
      toast.error('Eroare la configurarea autentificării în doi pași');
    }
  });

  // Verify 2FA setup
  const verifyMutation = useMutation({
    mutationFn: async () => {
      const response = await fetchWithAuth('/api/admin/auth/2fa/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: verificationCode }),
      });
      if (!response.ok) throw new Error('Invalid verification code');
      return response.json();
    },
    onSuccess: (data) => {
      toast.success(data.message);
      setSetupMode(false);
      setVerificationCode('');
      // Update the user data directly instead of invalidating
      queryClient.setQueryData(['current-user'], (oldData: any) => ({
        ...oldData,
        user: data.user
      }));
    },
    onError: () => {
      toast.error('Cod de verificare invalid');
    }
  });

  // Disable 2FA
  const disableMutation = useMutation({
    mutationFn: async () => {
      const response = await fetchWithAuth('/api/admin/auth/2fa/disable', {
        method: 'POST'
      });
      if (!response.ok) throw new Error('Failed to disable 2FA');
      return response.json();
    },
    onSuccess: (data) => {
      toast.success(data.message);
      // Update the user data directly instead of invalidating
      queryClient.setQueryData(['current-user'], (oldData: any) => ({
        ...oldData,
        user: data.user
      }));
    },
    onError: () => {
      toast.error('Eroare la dezactivarea autentificării în doi pași');
    }
  });

  if (!data?.user) return null;
  const user = data.user;

  return (
    <Card className="bg-white shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lock className="w-5 h-5" />
          Autentificare în Doi Pași
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            {user.twoFactorEnabled ? (
              <CheckCircle2 className="w-5 h-5 text-green-500" />
            ) : (
              <XCircle className="w-5 h-5 text-red-500" />
            )}
            <span className="font-medium">
              {user.twoFactorEnabled
                ? 'Autentificarea în doi pași este activată'
                : 'Autentificarea în doi pași este dezactivată'}
            </span>
          </div>
          {user.twoFactorEnabled && (
            <Button
              variant="destructive"
              onClick={() => disableMutation.mutate()}
              disabled={disableMutation.isPending}
            >
              {disableMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Dezactivează'
              )}
            </Button>
          )}
        </div>

        {!user.twoFactorEnabled && !setupMode && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Activați autentificarea în doi pași pentru a adăuga un nivel suplimentar de securitate contului dvs.
              Veți avea nevoie de o aplicație de autentificare precum Google Authenticator sau Authy.
            </p>
            <Button
              onClick={() => setupMutation.mutate()}
              disabled={setupMutation.isPending}
              className="flex items-center gap-2"
            >
              {setupMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <QrCode className="w-4 h-4" />
                  Configurează Autentificarea în Doi Pași
                </>
              )}
            </Button>
          </div>
        )}

        {setupMode && setupMutation.data && (
          <div className="space-y-6">
            <div className="space-y-2">
              <h3 className="font-medium">1. Scanați codul QR</h3>
              <p className="text-sm text-gray-600">
                Folosiți aplicația de autentificare pentru a scana acest cod QR
              </p>
              <div className="flex justify-center p-4 bg-gray-50 rounded-lg">
                <QRCode data={setupMutation.data.qrCode} />
              </div>
            </div>

            <div className="space-y-2">
              <h3 className="font-medium">2. Introduceți codul de verificare</h3>
              <p className="text-sm text-gray-600">
                Introduceți codul din aplicația de autentificare pentru a confirma configurarea
              </p>
              <div className="flex gap-4">
                <Input
                  type="text"
                  placeholder="Cod de verificare"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                />
                <Button
                  onClick={() => verifyMutation.mutate()}
                  disabled={verifyMutation.isPending || !verificationCode}
                  className="flex items-center gap-2"
                >
                  {verifyMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <KeyRound className="w-4 h-4" />
                      Verifică
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
